import './ChangePasswordFormConfig';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { IconButton, InputAdornment, Collapse, AlertColor, useTheme } from '@mui/material';
import React, { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { ChangePasswordFormRootStyles, PatternPassIcon, PatternNotPassIcon } from './styles/ChangePasswordFormStyles';
import { changePassword } from '../../../services/firebase.auth';
import { IPasswordValidation, passwordValidation } from '../../../utils/passwordValidationUtil';
import AtomAlert from '../../atoms/AtomAlert';
import AtomButton from '../../atoms/AtomButton/AtomButton';
import AtomGrid from '../../atoms/AtomGrid';
import AtomTextField from '../../atoms/AtomTextField/AtomTextField';
import AtomTypography from '../../atoms/AtomTypography/AtomTypography';
import FormBuilderComponent from '../../generics/FormBuilderComponent/FormBuilderComponent';
import { styleConfigs } from '../../resources/styleConfigs';

interface IFormValues {
  currentPassword: string;
  password: string;
  confirmPassword: string;
}

const initialValues: IFormValues = {
  currentPassword: '',
  password: '',
  confirmPassword: '',
};

interface IChangePasswordFormProps {
  closeModal: VoidFunction;
}

const ChangePasswordForm: React.FC<IChangePasswordFormProps> = ({ closeModal }: IChangePasswordFormProps) => {
  const [showCurrentPassword, setShowCurrentPassword] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [passwordPatternValidate, setPasswordPatternValidate] = useState<IPasswordValidation>({
    uppercase: false,
    number: false,
    length: false,
    spaces: false,
  });
  const [serverError, setServerError] = useState('');
  const [openAlert, setOpenAlert] = useState(false);
  const [severity, setSeverity] = useState<AlertColor>('error');
  const { t } = useTranslation('ChangePasswordForm');
  const theme = useTheme();

  const handleError = (error: unknown, isPasswordError = false): void => {
    setOpenAlert(true);
    setSeverity('error');

    if (error instanceof SyntaxError) {
      if (
        isPasswordError &&
        error.message &&
        (error.message.includes('wrong-password') || error.message.includes('user-not-found'))
      ) {
        setServerError(t('INVALID_USER_PASSWORD'));

        return;
      }
      setServerError(error.message);
    }
  };

  const updatePassword = async (password: string): Promise<void> => {
    try {
      await changePassword(password);
      formik.resetForm();
      setOpenAlert(true);
      setSeverity('success');
      setServerError(t('PASSWORD_CHANGED_SUCCESSFULLY'));
      setTimeout(() => {
        closeModal();
      }, 6000);
    } catch (error) {
      handleError(error);
    }
  };

  const login = async (password: string): Promise<void> => {
    try {
      await updatePassword(password);
    } catch (err) {
      handleError(err, true);
    }
  };

  const handleSubmit = async (values: IFormValues): Promise<void> => {
    setOpenAlert(false);
    login(values.password);
  };

  const formik = FormBuilderComponent(
    initialValues,
    Yup.object({
      currentPassword: Yup.string().required(t('CURRENT_PASSWORD_REQUIRED')),
      password: Yup.string(),
      confirmPassword: Yup.string().oneOf([Yup.ref('password'), null], t('PASSWORD_DONT_MATCH')),
    }),
    handleSubmit,
  );

  const passwordChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void =>
    setPasswordPatternValidate(passwordValidation(event?.target?.value));

  const renderPasswordConstraint = (isValid: boolean, constraintKey: string, testId: string): JSX.Element => (
    <AtomTypography
      fontWeight={400}
      sx={{
        display: 'flex',
        alignItems: 'center',
        fontSize: styleConfigs.fonts.fontSize16,
        color: isValid ? theme.palette.text.greenTeal : theme.palette.text.coralRed,
        marginBottom: styleConfigs.marginPadding.custom2,
        [theme.breakpoints.down('sm')]: {
          fontSize: styleConfigs.fonts.fontSize14,
        },
      }}
    >
      {t(constraintKey)}
      {isValid ? (
        <PatternPassIcon data-testid={`${testId}-pass`} />
      ) : (
        <PatternNotPassIcon data-testid={`${testId}-notpass`} />
      )}
    </AtomTypography>
  );

  const isPasswordValid = (): boolean =>
    passwordPatternValidate.uppercase &&
    passwordPatternValidate.number &&
    passwordPatternValidate.length &&
    passwordPatternValidate.spaces;

  const isFormValid = (): boolean =>
    isPasswordValid() &&
    !formik.errors.currentPassword &&
    !formik.errors.confirmPassword &&
    formik.values.confirmPassword !== '' &&
    formik.values.currentPassword !== '';

  return (
    <ChangePasswordFormRootStyles data-testid="changepassword-form" onSubmit={formik.handleSubmit}>
      <Collapse in={openAlert}>
        <AtomAlert severity={severity} variant="filled" onClose={() => setOpenAlert(false)} customSx={{ mb: 2 }}>
          {serverError}
        </AtomAlert>
      </Collapse>
      {severity !== 'success' && (
        <>
          <AtomTextField
            componentType="wrapper"
            type={showCurrentPassword ? 'text' : 'password'}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton edge="end" onClick={() => setShowCurrentPassword(!showCurrentPassword)}>
                    {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            id="current-password-input"
            label={t('LABEL_CURRENT_PASSWORD')}
            placeholder={t('LABEL_CURRENT_PASSWORD')}
            autoFocus
            name="currentPassword"
            onChange={formik.handleChange}
            error={!!formik.errors.currentPassword || false}
            helperText={formik.errors.currentPassword}
            required
            color="secondary"
            sx={{
              marginBottom: styleConfigs.marginPadding.custom24rem,
              '&  > .MuiOutlinedInput-root': {
                '& > .MuiOutlinedInput-input': {
                  fontWeight: styleConfigs.fonts.bold,
                  color: theme.palette.text.ebonyClay,
                },
              },
              '& > .MuiInputLabel-root': {
                fontWeight: styleConfigs.fonts.bold,
                color: theme.palette.text.amethyst,
              },
            }}
          />
          <AtomTextField
            componentType="wrapper"
            type={showPassword ? 'text' : 'password'}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton edge="end" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            id="new-password-input"
            label={t('LABEL_NEW_PASSWORD')}
            placeholder={t('LABEL_NEW_PASSWORD')}
            name="password"
            onChange={(event) => {
              formik.handleChange(event);
              passwordChange(event);
            }}
            error={!isPasswordValid() && formik.values.password !== ''}
            required
            color="secondary"
            sx={{
              marginBottom: styleConfigs.marginPadding.custom24rem,
              '&  > .MuiOutlinedInput-root': {
                '& > .MuiOutlinedInput-input': {
                  fontWeight: styleConfigs.fonts.bold,
                  color: theme.palette.text.ebonyClay,
                },
              },
              '& > .MuiInputLabel-root': {
                fontWeight: styleConfigs.fonts.bold,
                color: theme.palette.text.amethyst,
              },
            }}
          />
          <AtomTextField
            componentType="wrapper"
            type={showConfirmPassword ? 'text' : 'password'}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton edge="end" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            id="confirm-password-input"
            label={t('LABEL_CONFIRM_NEW_PASSWORD')}
            placeholder={t('LABEL_CONFIRM_NEW_PASSWORD')}
            name="confirmPassword"
            onChange={formik.handleChange}
            error={!!formik.errors.confirmPassword && formik.values.confirmPassword !== ''}
            helperText={formik.errors.confirmPassword === t('PASSWORD_DONT_MATCH') ? formik.errors.confirmPassword : ''}
            required
            color="secondary"
            sx={{
              marginBottom: styleConfigs.marginPadding.custom24rem,
              '&  > .MuiOutlinedInput-root': {
                '& > .MuiOutlinedInput-input': {
                  fontWeight: styleConfigs.fonts.bold,
                  color: theme.palette.text.ebonyClay,
                },
              },
              '& > .MuiInputLabel-root': {
                fontWeight: styleConfigs.fonts.bold,
                color: theme.palette.text.amethyst,
              },
            }}
          />
          <AtomGrid marginBottom={styleConfigs.marginPadding.custom3}>
            <AtomTypography
              fontWeight={600}
              sx={{
                fontSize: styleConfigs.fonts.fontSize16,
                color: theme.palette.text.ebonyClay,
                marginBottom: styleConfigs.marginPadding.custom12rem,
                [theme.breakpoints.down('sm')]: {
                  fontSize: styleConfigs.fonts.fontSize14,
                },
              }}
            >
              {t('CONSTRAINT_CONTAIN')}
            </AtomTypography>
            {renderPasswordConstraint(passwordPatternValidate.uppercase, 'CONSTRAINT_UPPERCASE', 'uppercase')}
            {renderPasswordConstraint(passwordPatternValidate.number, 'CONSTRAINT_NUMBER', 'number')}
            {renderPasswordConstraint(passwordPatternValidate.length, 'CONSTRAINT_LENGTH', 'length')}
            {renderPasswordConstraint(passwordPatternValidate.spaces, 'CONSTRAINT_SPACES', 'spaces')}
          </AtomGrid>
          <AtomButton
            type="submit"
            variant="contained"
            color="secondary"
            disabled={!isFormValid()}
            sx={{
              height: styleConfigs.dimensions.height48,
              fontSize: styleConfigs.fonts.fontSize15,
              borderRadius: styleConfigs.borders.radius8rem,
              boxShadow: `${styleConfigs.marginPadding.empty} ${styleConfigs.marginPadding.custom8}px 
                ${styleConfigs.marginPadding.custom16}px ${theme.palette.boxShadows.daySkyBlueA80}`,
              '&:hover': {
                boxShadow: `${styleConfigs.marginPadding.empty} ${styleConfigs.marginPadding.custom8}px 
                  ${styleConfigs.marginPadding.custom16}px ${theme.palette.boxShadows.daySkyBlueA80}`,
              },
            }}
          >
            {t('UPDATE_PASSWORD')}
          </AtomButton>
        </>
      )}
    </ChangePasswordFormRootStyles>
  );
};

export default ChangePasswordForm;
